import React, { useCallback, useEffect, useState, useMemo } from "react";
import { api, fmtDate } from "../lib/api";
import { PageHeader, Surface, Btn, Field, Input, Select, Textarea, StatusTag } from "../components/Primitives";
import { useAuth } from "../contexts/AuthContext";
import { Plus, X, CaretLeft, CaretRight, NotePencil } from "@phosphor-icons/react";

const monthLabel = (d) => d.toLocaleString("en-GB", { month: "long", year: "numeric" });
const dayLabel = (d) => d.toLocaleString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
const HOURS = Array.from({ length: 16 }, (_, i) => i + 6); // 06:00 .. 21:00
const todayStr = () => new Date().toISOString().substring(0, 10);

export default function CalendarPage() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner" || user?.role === "tenant_admin";
  const [bookings, setBookings] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [filter, setFilter] = useState("");
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState("month"); // month | week | day
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ aircraft_id: "", start_dt: "", end_dt: "", type: "flight", notes: "", instructor: "" });
  const [err, setErr] = useState("");
  const [detail, setDetail] = useState(null); // selected booking
  const [logOpen, setLogOpen] = useState(false);
  const [logForm, setLogForm] = useState(null);
  const [logErr, setLogErr] = useState("");

  const load = useCallback(() => Promise.all([
    api.get(filter ? `/bookings?aircraft_id=${filter}` : "/bookings").then((r) => setBookings(r.data)),
    api.get("/aircraft").then((r) => setAircraft(r.data)),
    api.get("/memberships").then((r) => setMemberships(r.data)),
    api.get("/settlements").then((r) => setSettlements(r.data)),
  ]), [filter]);
  useEffect(() => { load(); }, [load]);

  const bookableAircraft = useMemo(() => {
    if (isOwner) return aircraft;
    const allowedIds = new Set(memberships.filter((m) => ["active", "training_only"].includes(m.status)).map((m) => m.aircraft_id));
    return aircraft.filter((a) => allowedIds.has(a.id) && a.status !== "grounded");
  }, [aircraft, memberships, isOwner]);

  const settlementsByBooking = useMemo(() => {
    const map = {};
    for (const s of settlements) {
      if (s.flight_log_id) {
        // We don't have flight_log_id directly mapped; but bookings have settlement_id
      }
    }
    for (const b of bookings) {
      if (b.settlement_id) {
        const s = settlements.find((x) => x.id === b.settlement_id);
        if (s) map[b.id] = s;
      }
    }
    return map;
  }, [bookings, settlements]);

  const days = useMemo(() => {
    if (view === "day") return [new Date(cursor)];
    const total = view === "month" ? 42 : 7;
    let base;
    if (view === "month") {
      const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const offset = (start.getDay() + 6) % 7;
      base = new Date(start.getFullYear(), start.getMonth(), 1 - offset);
    } else {
      base = new Date(cursor);
      const wd = (base.getDay() + 6) % 7;
      base.setDate(base.getDate() - wd);
    }
    const arr = [];
    for (let i = 0; i < total; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [cursor, view]);

  const bookingsByDay = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      const k = b.start_dt.substring(0, 10);
      (map[k] = map[k] || []).push(b);
    }
    return map;
  }, [bookings]);

  const dayBookings = useMemo(() => {
    const k = cursor.toISOString().substring(0, 10);
    return (bookingsByDay[k] || []).slice().sort((a, b) => a.start_dt.localeCompare(b.start_dt));
  }, [bookingsByDay, cursor]);

  const save = async () => {
    setErr("");
    try {
      if (!form.aircraft_id || !form.start_dt || !form.end_dt) return;
      await api.post("/bookings", form);
      setOpen(false);
      setForm({ aircraft_id: "", start_dt: "", end_dt: "", type: "flight", notes: "", instructor: "" });
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to create booking");
    }
  };

  const colorFor = (b) => {
    if (b.status === "completed") return "border-l-green-500 bg-green-500/5 text-green-300";
    if (b.status === "awaiting_settlement") return "border-l-amber bg-[#FFB00022] text-amber";
    return b.type === "training" ? "border-l-amber bg-[#FFB00011]"
      : b.type === "maintenance" ? "border-l-red-500 bg-red-500/5"
      : "border-l-[#0A84FF] bg-[#0A84FF11]";
  };

  const openLogFromBooking = (b) => {
    setLogErr("");
    const dur = Math.max(0.5, (new Date(b.end_dt) - new Date(b.start_dt)) / 3600000);
    setLogForm({
      booking_id: b.id,
      aircraft_id: b.aircraft_id,
      aircraft_registration: b.aircraft_registration,
      user_id: b.user_id,
      user_name: b.user_name,
      flight_date: b.start_dt.substring(0, 10),
      departure: "",
      arrival: "",
      hobbs_start: 0,
      hobbs_end: Number(dur.toFixed(1)),
      fuel_start: 0,
      fuel_end: 0,
      fuel_added: 0,
      oil_added: 0,
      squawks: "",
      is_training: b.type === "training",
      instructor_hours: 0,
    });
    setDetail(null);
    setLogOpen(true);
  };

  const submitLog = async () => {
    setLogErr("");
    try {
      await api.post("/flight-logs", { ...logForm, confirmed: true });
      setLogOpen(false);
      setLogForm(null);
      load();
    } catch (e) {
      setLogErr(e.response?.data?.detail || "Failed to save flight log");
    }
  };

  const settleStatusTag = (b) => {
    const s = settlementsByBooking[b.id];
    if (b.status === "awaiting_settlement") {
      return <span className="tag tag-amber" data-testid={`booking-awaiting-${b.id}`}>Awaiting settlement</span>;
    }
    if (b.status === "completed" && s) {
      return <span className={`tag ${s.status === "confirmed" ? "tag-green" : s.status === "sent" ? "tag-blue" : "tag-amber"}`}>{s.status === "confirmed" ? "Paid" : s.status === "sent" ? "Sent" : "Awaiting"}</span>;
    }
    return null;
  };

  const deleteBooking = async (bid) => {
    if (!window.confirm("Delete this booking? This cannot be undone.")) return;
    try {
      await api.delete(`/bookings/${bid}`);
      setDetail(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to delete booking");
    }
  };

  const rebillBooking = async (b) => {
    // Re-create the settlement against the existing flight log (kept after settlement deletion).
    try {
      await api.post("/settlements", { booking_id: b.id });
      setDetail(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to re-bill booking");
    }
  };

  return (
    <div>
      <PageHeader overline="Operations" title="Booking calendar"
        subtitle="Aircraft availability across the private group. Members can only book aircraft they belong to."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-10 bg-[#0A0A0A] border border-zinc-800 px-3 text-sm font-mono">
              <option value="">All aircraft</option>
              {aircraft.map((a) => <option key={a.id} value={a.id}>{a.registration}</option>)}
            </select>
            <div className="flex border border-zinc-800">
              {["month", "week", "day"].map((v) => (
                <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`}
                  className={`px-3 h-10 text-xs uppercase tracking-[0.15em] ${view === v ? "bg-amber text-black" : "text-zinc-300 hover:text-amber"}`}>{v}</button>
              ))}
            </div>
            <Btn data-testid="new-booking-btn" onClick={() => setOpen(true)}><Plus size={14} className="inline mr-1.5" />New booking</Btn>
          </div>
        }
      />
      <div className="p-8">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => {
            const d = new Date(cursor);
            if (view === "day") d.setDate(d.getDate() - 1);
            else if (view === "week") d.setDate(d.getDate() - 7);
            else d.setMonth(d.getMonth() - 1);
            setCursor(d);
          }} className="p-2 border border-zinc-800 hover:border-amber" data-testid="prev-btn"><CaretLeft size={14} /></button>
          <div className="font-mono text-lg amber tracking-tight">
            {view === "day" ? dayLabel(cursor) : monthLabel(cursor)}
          </div>
          <button onClick={() => {
            const d = new Date(cursor);
            if (view === "day") d.setDate(d.getDate() + 1);
            else if (view === "week") d.setDate(d.getDate() + 7);
            else d.setMonth(d.getMonth() + 1);
            setCursor(d);
          }} className="p-2 border border-zinc-800 hover:border-amber" data-testid="next-btn"><CaretRight size={14} /></button>
        </div>

        {view === "day" ? (
          <Surface className="overflow-hidden" data-testid="day-view">
            <div className="grid" style={{ gridTemplateColumns: "70px 1fr" }}>
              {HOURS.map((h) => {
                const slotBookings = dayBookings.filter((b) => {
                  const sh = new Date(b.start_dt).getHours();
                  const eh = new Date(b.end_dt).getHours();
                  return h >= sh && h < (eh === sh ? eh + 1 : eh);
                });
                return (
                  <React.Fragment key={h}>
                    <div className="border-b border-r border-zinc-900 p-2 text-[11px] font-mono text-zinc-500">{String(h).padStart(2, "0")}:00</div>
                    <div className="border-b border-zinc-900 p-2 min-h-[64px] space-y-1">
                      {slotBookings.map((b) => (
                        <button key={b.id} onClick={() => setDetail(b)}
                          className={`w-full text-left px-2 py-1.5 border-l-2 text-xs transition-colors ${colorFor(b)} hover:bg-amber/10`}>
                          <div className="flex justify-between">
                            <span><span className="font-mono text-amber mr-2">{b.aircraft_registration}</span>{b.user_name}</span>
                            <span className="font-mono text-zinc-500">{b.start_dt.substring(11, 16)} → {b.end_dt.substring(11, 16)}</span>
                          </div>
                          <div className="flex gap-2 items-center mt-0.5">
                            <StatusTag status={b.type} />
                            {settleStatusTag(b)}
                            {b.notes && <span className="text-zinc-500 text-[10px] truncate">{b.notes}</span>}
                          </div>
                        </button>
                      ))}
                      {slotBookings.length === 0 && <div className="text-[10px] text-zinc-700 font-mono uppercase tracking-wider">—</div>}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </Surface>
        ) : (
          <Surface className="overflow-hidden">
            <div className="grid grid-cols-7 border-b border-zinc-800">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                <div key={d} className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono p-3 border-r border-zinc-900">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {days.map((d, _i) => {
                const k = d.toISOString().substring(0, 10);
                const items = bookingsByDay[k] || [];
                const inMonth = view === "week" || d.getMonth() === cursor.getMonth();
                const isToday = k === todayStr();
                return (
                  <div key={k} onClick={() => { setCursor(new Date(d)); setView("day"); }}
                    className={`min-h-[100px] p-2 border-b border-r border-zinc-900 cursor-pointer hover:bg-[#1a1a1a] ${inMonth ? "" : "opacity-40"}`}>
                    <div className={`font-mono text-xs mb-1 ${isToday ? "text-amber" : "text-zinc-400"}`}>{d.getDate()}</div>
                    <div className="space-y-1">
                      {items.slice(0, 3).map((b) => (
                        <div key={b.id} className={`text-[10px] px-1.5 py-1 border-l-2 truncate ${colorFor(b)}`}>
                          <span className="font-mono">{b.aircraft_registration}</span> · {b.user_name}
                        </div>
                      ))}
                      {items.length > 3 && <div className="text-[10px] text-zinc-500">+{items.length - 3} more</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </Surface>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="booking-modal">
          <Surface className="w-full max-w-xl p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-medium">New booking</h3>
              <button onClick={() => setOpen(false)}><X size={20} className="text-zinc-500" /></button>
            </div>
            <div className="space-y-4">
              <Field label="Aircraft" hint={!isOwner ? "Only aircraft you are an active member of are listed." : null}>
                <Select data-testid="booking-aircraft" value={form.aircraft_id} onChange={(e) => setForm({ ...form, aircraft_id: e.target.value })}>
                  <option value="">Select…</option>
                  {bookableAircraft.map((a) => <option key={a.id} value={a.id}>{a.registration} · {a.make_model}</option>)}
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Start"><Input data-testid="booking-start" type="datetime-local" value={form.start_dt} onChange={(e) => setForm({ ...form, start_dt: e.target.value })} /></Field>
                <Field label="End"><Input data-testid="booking-end" type="datetime-local" value={form.end_dt} onChange={(e) => setForm({ ...form, end_dt: e.target.value })} /></Field>
              </div>
              <Field label="Type">
                <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option value="flight">Flight</option><option value="training">Training</option><option value="maintenance">Maintenance</option>
                </Select>
              </Field>
              <Field label="Instructor (optional)"><Input value={form.instructor} onChange={(e) => setForm({ ...form, instructor: e.target.value })} /></Field>
              <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
            </div>
            {err && <div className="mt-4 text-sm text-red-400 font-mono" data-testid="booking-error">{err}</div>}
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
              <Btn onClick={save} data-testid="booking-save-btn">Confirm booking</Btn>
            </div>
          </Surface>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="booking-detail">
          <Surface className="w-full max-w-lg p-8">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1">Booking</div>
                <div className="font-mono text-2xl text-amber">{detail.aircraft_registration}</div>
                <div className="text-sm text-zinc-300 mt-1">{detail.user_name}</div>
              </div>
              <button onClick={() => setDetail(null)}><X size={20} className="text-zinc-500" /></button>
            </div>
            <div className="space-y-2 text-sm font-mono mb-6">
              <div className="text-zinc-300">{fmtDate(detail.start_dt)} · {detail.start_dt.substring(11, 16)} → {detail.end_dt.substring(11, 16)}</div>
              <div className="flex gap-2"><StatusTag status={detail.type} /><StatusTag status={detail.status} />{settleStatusTag(detail)}</div>
              {detail.instructor && <div className="text-xs text-zinc-500">Instructor: {detail.instructor}</div>}
              {detail.notes && <div className="text-xs text-zinc-400">{detail.notes}</div>}
              {settlementsByBooking[detail.id] && (
                <div className="mt-3 pt-3 border-t border-zinc-800">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Settlement</div>
                  <div className="text-amber">{settlementsByBooking[detail.id].reference}</div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 flex-wrap">
              {(detail.user_id === user?.user_id || isOwner) && (
                <Btn variant="ghost" onClick={() => deleteBooking(detail.id)} data-testid="delete-booking-btn">
                  <X size={14} className="inline mr-1.5" />Delete booking
                </Btn>
              )}
              {detail.status === "awaiting_settlement" && isOwner && (
                <Btn onClick={() => rebillBooking(detail)} data-testid="rebill-booking-btn">
                  <NotePencil size={14} className="inline mr-1.5" />Create new settlement
                </Btn>
              )}
              {detail.status !== "completed" && detail.status !== "awaiting_settlement" && (
                <Btn onClick={() => openLogFromBooking(detail)} data-testid="log-from-booking">
                  <NotePencil size={14} className="inline mr-1.5" />Log flight & generate settlement
                </Btn>
              )}
            </div>
          </Surface>
        </div>
      )}

      {logOpen && logForm && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="quick-log-modal">
          <Surface className="w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-xl font-medium">Log flight from booking</h3>
                <div className="text-sm text-zinc-500 mt-1">{logForm.aircraft_registration} · {logForm.user_name} · {fmtDate(logForm.flight_date)}</div>
              </div>
              <button onClick={() => setLogOpen(false)}><X size={20} className="text-zinc-500" /></button>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Departure (ICAO)"><Input value={logForm.departure} onChange={(e) => setLogForm({ ...logForm, departure: e.target.value.toUpperCase() })} /></Field>
              <Field label="Arrival (ICAO)"><Input value={logForm.arrival} onChange={(e) => setLogForm({ ...logForm, arrival: e.target.value.toUpperCase() })} /></Field>
              <Field label="Instructor hours"><Input type="number" step="0.1" value={logForm.instructor_hours} onChange={(e) => setLogForm({ ...logForm, instructor_hours: parseFloat(e.target.value || 0) })} /></Field>
              <Field label="Hobbs start"><Input type="number" step="0.1" value={logForm.hobbs_start} onChange={(e) => setLogForm({ ...logForm, hobbs_start: parseFloat(e.target.value || 0) })} /></Field>
              <Field label="Hobbs end"><Input type="number" step="0.1" value={logForm.hobbs_end} onChange={(e) => setLogForm({ ...logForm, hobbs_end: parseFloat(e.target.value || 0) })} /></Field>
              <Field label="Fuel start (L)"><Input type="number" step="0.1" value={logForm.fuel_start} onChange={(e) => setLogForm({ ...logForm, fuel_start: parseFloat(e.target.value || 0) })} /></Field>
              <Field label="Fuel end (L)"><Input type="number" step="0.1" value={logForm.fuel_end} onChange={(e) => setLogForm({ ...logForm, fuel_end: parseFloat(e.target.value || 0) })} /></Field>
            </div>
            {logErr && <div className="mt-3 text-sm text-red-400 font-mono">{logErr}</div>}
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => setLogOpen(false)}>Cancel</Btn>
              <Btn onClick={submitLog} data-testid="quick-log-submit">Save & generate settlement</Btn>
            </div>
          </Surface>
        </div>
      )}
    </div>
  );
}
